import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useQueryState } from 'nuqs'

import { motion } from 'motion/react'

import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import FormUIToggle from '~/components/UI/Forms/Toggle/Toggle'
import { useDebounce } from '~/hooks/useDebounce'

import { default as SearchIcon } from '~/components/UI/Forms/ComboBox/icon/search'
import { TestNavSearch } from '../../data/testData'
import { Link } from 'react-router'
import { cn } from '~/lib/utils'

interface SearchProps {
  textColour?: string
  onFocus?: () => void
  onBlur?: () => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const Search = (props: SearchProps) => {
  // const navigate = useNavigate()
  const { textColour, onFocus, onBlur } = props
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 400)

  const [type, setType] = useQueryState('type', {
    defaultValue: 'all'
  })

  const { data, isFetched, isSuccess, isFetching } = useQuery({
    queryKey: ['NavSearchData', debouncedSearchTerm, type, 5],
    queryFn: () => TestNavSearch(debouncedSearchTerm, type, 5),
    enabled: debouncedSearchTerm.trim().length > 0
  })

  const { data: freqData } = useQuery({
    queryKey: ['frequentVisits'],
    queryFn: () => mockFreqData
  })

  const container = {
    hidden: { opacity: 0, height: 0 },
    show: {
      opacity: 1,
      height: 'auto',
      transition: {
        staggerChildren: 0.3
      }
    }
  }

  const listItem = {
    hidden: { opacity: 0 },
    show: { opacity: 1 }
  }

  return (
    <>
      <h2 className='text-lg font-semibold'>Search</h2>

      <div className='space-y-xs pb-xs'>
        <div className='w-fit'>
          <FormUIToggle
            type='single'
            format='text'
            data={[
              { id: 'all', label: 'All' },
              { id: 'courses', label: 'Courses' },
              { id: 'research', label: 'Research' },
              { id: 'people', label: 'Staff' }
            ]}
            className='flex-wrap justify-start'
            itemClass='hover:bg-black/20 data-[state=on]:bg-blue-m data-[state=on]:text-white'
            value={type}
            onValueChange={value => {
              if (typeof value === 'string') setType(value)
            }}
          />
        </div>

        <div className='relative flex items-center'>
          <div className='p-xs'>
            <SearchIcon
              fill={textColour === 'text-white' ? '#ffffff' : '#0e1647'}
              width={'1.5rem'}
              height={'1.5rem'}
              title={''}
            />
          </div>
          <input
            name='courseSearch'
            aria-label='Search for a courses'
            className='pl-l p-xs text-base-sm placeholder:text-blue-d-60 disabled:bg-grey-m disabled:placeholder:text-blue-d-80 sm:text-base-md lg:text-base-lg dark:bg-blue-d/50 focus-visible:ring-offset-dark-purple-80 dark:ring-offset-purplering-offset-dark-purple-80 dark:focus-visible:ring-purple-40 absolute flex w-full border-b ring-offset-white focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed dark:placeholder:text-white/80'
            onChange={e => setSearchTerm(e.target.value)}
            type='search'
            placeholder='Site search'
            value={searchTerm}
            onFocus={onFocus}
            onBlur={onBlur}
            // -- remove blur and focus on enter keypress -- //
            // onKeyDown={e => {
            //   if (e.key === 'Enter') {
            //     e.preventDefault()
            //     if (debouncedSearchTerm.trim()) {
            //       navigate(
            //         type && type !== 'all'
            //           ? `/search/${type}?q=${searchTerm}`
            //           : `/search?q=${searchTerm}`
            //       )
            //     }
            //   }
            // }}
          />
        </div>
      </div>

      {!searchTerm && (
        // && Array.isArray(data) && data.length <= 0
        <div className='space-y-m'>
          <p className='tag text-blue-d'>Frequently searched</p>
          <motion.ul
            className='space-y-s grid list-none grid-cols-1 pl-0 sm:grid-cols-2'
            variants={container}
            initial='hidden'
            animate='show'
          >
            {freqData &&
              freqData.map(
                (item: {
                  sys: { id: Index; uri: string }
                  entryTitle: string
                }) => (
                  <li
                    key={item.sys.id}
                    className={cn(
                      // `*:${textColour}`,
                      'lg:*:text-blue-d block w-fit'
                    )}
                  >
                    <TertiaryLink
                      data={{
                        strongColour: false,
                        externalLink: null,
                        internalLink: { sys: { uri: item.sys.uri } },
                        ariaLabel: item.entryTitle,
                        newWindow: false,
                        label: item.entryTitle
                      }}
                      colour={'ink-blue'}
                      // colour={textColour === 'text-white' ? 'white' : 'ink-blue'}
                    />
                  </li>
                )
              )}
          </motion.ul>
        </div>
      )}

      {isFetching && <p>Loading...</p>}

      {isFetched && isSuccess && (
        <div className='space-y-m max-h-[500px] max-w-[500px] overflow-auto'>
          <motion.ul
            variants={container}
            initial='hidden'
            animate='show'
            className='space-y-xs list-none pl-0'
          >
            {searchTerm !== '' &&
              Array.isArray(data) &&
              data.map(item => (
                <motion.li variants={listItem} key={item?.sys?.id}>
                  <Link
                    to={{ pathname: item?.sys?.uri }}
                    className={cn(
                      // textColour,
                      'unstyled-link group text-blue-d'
                    )}
                    reloadDocument
                  >
                    <p className='line-clamp-2 font-bold group-hover:underline'>
                      {item.seoStandard.title}
                    </p>
                    <p className='line-clamp-2 max-w-[80ch]'>
                      {item.seoStandard.description}
                    </p>
                  </Link>
                </motion.li>
              ))}

            {Array.isArray(data) && data.length <= 0 && (
              <li className='max-w-fit'>
                Your query{' '}
                <strong className='tracking-wider'>
                  {'"' + searchTerm + '"'}
                </strong>{' '}
                returned 0 results, please try a different search term
              </li>
            )}
          </motion.ul>
          {Array.isArray(data) && data.length >= 5 && (
            <TertiaryLink
              data={{
                strongColour: false,
                externalLink: null,
                internalLink: {
                  sys: {
                    uri:
                      type && type !== 'all'
                        ? `/search/${type}?q=${searchTerm}`
                        : `/search?q=${searchTerm}`
                  }
                },
                ariaLabel: 'Search results',
                newWindow: false,
                label: 'View all search results'
              }}
              colour={'ink-blue'}
              className='text-blue-d'
            />
          )}
        </div>
      )}
    </>
  )
}

export default Search

export const mockFreqData = [
  {
    entryTitle: 'Clearing',
    sys: {
      uri: '/search?q=clearing',
      id: 1
    }
  },
  {
    entryTitle: 'Business',
    sys: {
      uri: '/search?q=business',
      id: 2
    }
  },
  {
    entryTitle: 'Law',
    sys: {
      uri: '/search?q=law',
      id: 3
    }
  },
  {
    entryTitle: 'Nursing',
    sys: {
      uri: '/search?q=nursing',
      id: 4
    }
  },
  {
    entryTitle: 'Social Work',
    sys: {
      uri: '/search?q=social+work',
      id: 5
    }
  },
  {
    entryTitle: 'Engineering',
    sys: {
      uri: '/search?q=engineering',
      id: 6
    }
  }
]
